<template>
  <form @submit.prevent="save">
    <error-display v-if="errorMessage">{{errorMessage}}</error-display>
    <div class="content">
      <div class="row">
        <input-text name="firstName" label="First Name" class="col-md-6" v-model="user.firstname" :validation="$v.user.firstname"></input-text>
        <input-text name="lastName" label="Last Name" class="col-md-6" v-model="user.lastname" :validation="$v.user.lastname"></input-text>
      </div>
      <div class="row">
        <input-text name="emailAddress" label="Email Address" v-model="user.emailAddress" :validation="$v.user.emailAddress"></input-text>
      </div>
      <div class="row">
        <input-text name="cellularAddress" label="Cellular Number" v-model="user.cellularNumber" :validation="$v.user.cellularNumber"></input-text>
      </div>
      <div class="well">
        <h4 class="padding-bottom-10">Username</h4>

        <div class="radio">
          <label>
            <input type="radio" id="usernameTypeEmail" name="usernameType" value="EMAIL" v-model="usernameType">
            Use Email Address<span v-if="usernameType === 'EMAIL'">: {{user.emailAddress}}</span>
          </label>
        </div>
        <div class="radio">
          <label>
            <input type="radio" id="usernameTypeCellular" name="usernameType" value="CELLULAR" v-model="usernameType">
            Use Cellular Number<span v-if="usernameType === 'CELLULAR'">: {{user.cellularNumber}}</span>
          </label>
        </div>
        <div class="radio">
          <label>
            <input type="radio" id="usernameTypeCustom" name="usernameType" value="CUSTOM" v-model="usernameType" @click="clearUsername">
            Create your own
          </label>
        </div>

        <div class="row" v-if="usernameType === 'CUSTOM'">
          <input-text name="username" label="Username" v-model="user.username" :validation="$v.user.username"></input-text>
        </div>
      </div>
      <div class="row">
        <input-password name="password" label="Password" v-model="user.password" :validation="$v.user.password"></input-password>
      </div>
      <div class="row">
        <input-password name="confirmPassword" label="Confirm Password" v-model="confirmPassword" :validation="$v.confirmPassword"></input-password>
      </div>
      <div class="row">
        <div class="col-xs-4">
          <div class="row">
            <input-switch name="identityType" v-model="user.identityType" :options="identityTypes"></input-switch>
          </div>
        </div>
        <div class="col-xs-8" v-if="user.identityType === 'IDDOC'">
          <div class="row">
            <input-text name="identityNumber" label="Identity Number" v-model="user.identityNumber" :validation="$v.user.identityNumber"></input-text>
          </div>
        </div>
        <div class="col-xs-8" v-else-if="user.identityType === 'PASSPORT'">
          <div class="row">
            <input-text name="passportNumber" label="Passport Number" v-model="user.passportNumber" :validation="$v.user.passportNumber"></input-text>
            <input-select name="passportCountry" label="Passport Country" v-model="user.passportCountry" :options="countries" :validation="$v.user.passportCountry" :loading="loadingCountries"></input-select>
          </div>
        </div>
      </div>
      <span v-if="user.type === 'HCP'">
        <div class="row">
          <input-select name="discipline" label="Select Discipline" v-model="user.hcp.discipline" :options="disciplines" :validation="$v.user.hcp.discipline" @change.native="loadSpecialities" :loading="loadingDisciplines"></input-select>
        </div>
        <div class="row" v-if="user.hcp.discipline">
          <input-select name="speciality" label="Select Speciality" v-model="user.hcp.speciality" :options="specialities" :validation="$v.user.hcp.speciality" @change.native="loadRoles" :loading="loadingSpecialities"></input-select>
        </div>
        <div class="row" v-if="user.hcp.speciality">
          <input-select name="role" label="Select Role" v-model="user.hcp.role" :options="roles" :validation="$v.user.hcp.role" :loading="loadingRoles"></input-select>
        </div>
        <div class="row">
          <input-text name="hpcsa" label="HPCSA Number" v-model="user.hcp.hpcsaNumber" :validation="$v.user.hcp.hpcsaNumber"></input-text>
        </div>
        <div class="row" v-if="!user.inviteCode">
          <input-text name="practiceNumber" label="Practice Number" v-model="user.practiceNumber" :validation="$v.user.practiceNumber"></input-text>
        </div>
        <div class="row" v-if="!user.inviteCode">
          <input-text name="practicePhone" label="Practice Phone Number" v-model="user.phone.number" :validation="$v.user.phone.number"></input-text>
        </div>
      </span>
    </div>
    <div class="footer">
      <div class="row">
        <div class="input-group col-sm-offset-3">
          <input type="checkbox" name="terms" required> I have read and accept the
            <a class="highlight" target="_blank" @click.prevent="openModal">Terms and Conditions</a>
        </div>
      </div>
      <div class="row">
        <div class="col-xs-12 text-center">
          <submit-button id="signUpSubmit" :loading="loading">Register</submit-button>
        </div>
      </div>
    </div>

    <ud-terms-modal :open="modalOpen" @close="closeModal" id="openTermsModal"></ud-terms-modal>
  </form>
</template>

<script>
  import { required, sameAs, email, maxLength, minLength, numeric } from 'vuelidate/lib/validators';
  import { saIdNumber, phoneNumber, requiredIf } from 'utils/validators';

  import InputText from 'components/common/inputs/InputText';
  import InputPassword from 'components/common/inputs/InputPassword';
  import InputSelect from 'components/common/inputs/InputSelect';
  import InputSwitch from 'components/common/inputs/InputSwitch';

  import SubmitButton from 'components/common/buttons/SubmitButton';

  import ErrorDisplay from 'components/common/outputs/ErrorDisplay';

  export default {
    name: 'practice-form',
    components: {
      InputText,
      InputPassword,
      InputSelect,
      InputSwitch,
      SubmitButton,
      ErrorDisplay,
      UdTermsModal: () => import('components/common/modals/UdTermsModal'),
    },
    data: () => ({
      loading: false,
      errorMessage: '',
      countries: [],
      disciplines: [],
      specialities: [],
      roles: [],
      user: {
        username: '',
        password: '',
        firstname: '',
        lastname: '',
        emailAddress: '',
        cellularNumber: '',
        practicePhone: '',
        identityType: 'IDDOC',
        identityNumber: '',
        passportNumber: '',
        passportCountry: undefined,
        practiceNumber: '',
        phone: {
          number: '',
        },
        hcp: {
          discipline: undefined,
          speciality: undefined,
          role: undefined,
          hpcsaNumber: '',
        },
        inviteCode: undefined,
        type: 'HCP',
      },
      usernameType: 'CUSTOM',
      confirmPassword: '',
      identityTypes: [
        {
          name: 'ID Document',
          value: 'IDDOC',
        },
        {
          name: 'Passport',
          value: 'PASSPORT',
        },
      ],
      modalOpen: false,
    }),
    validations: {
      user: {
        firstname: { required },
        lastname: { required },
        emailAddress: {
          required,
          email,
        },
        cellularNumber: {
          required,
          phoneNumber,
        },
        username: { required },
        password: { required },
        identityNumber: {
          requiredIf: requiredIf(user => user.identityType === 'IDDOC'),
          saIdNumber,
        },
        passportNumber: {
          requiredIf: requiredIf(user => user.identityType === 'PASSPORT'),
        },
        passportCountry: {
          requiredIf: requiredIf(user => user.identityType === 'PASSPORT'),
        },
        practiceNumber: {
          requiredIf: requiredIf(function () {
            return this.hcpSignup && !this.userInvited;
          }),
          maxLength: maxLength(7),
          minLength: minLength(7),
          numeric,
        },
        phone: {
          number: {
            phoneNumber,
            requiredIf: requiredIf(function () {
              return this.hcpSignup && !this.userInvited;
            }),
          },
        },
        hcp: {
          discipline: {
            requiredIf: requiredIf(function () {
              return this.hcpSignup;
            }),
          },
          speciality: {
            requiredIf: requiredIf(function () {
              return this.hcpSignup;
            }),
          },
          role: {
            requiredIf: requiredIf(function () {
              return this.hcpSignup;
            }),
          },
          hpcsaNumber: {
            requiredIf: requiredIf(function () {
              return this.hcpSignup;
            }),
          },
        },
      },
      confirmPassword: {
        required,
        sameAsPassword: sameAs(data => data.user.password),
      },
    },
    computed: {
      userInvited() {
        return this.user.inviteCode !== undefined;
      },
      hcpSignup() {
        return this.user.type === 'HCP';
      },
      loadingCountries() {
        return this.countries.length === 0;
      },
      loadingDisciplines() {
        return this.disciplines.length === 0;
      },
      loadingSpecialities() {
        return this.specialities.length === 0;
      },
      loadingRoles() {
        return this.roles.length === 0;
      },
    },
    methods: {
      clearUsername() {
        this.user.username = '';
      },
      clearIdentificationData() {
        switch (this.user.identityType) {
          case 'IDDOC':
            this.user.passportNumber = '';
            this.user.passportCountry = undefined;
            break;
          case 'PASSPORT':
            this.user.identityNumber = '';
            break;
          default:
            break;
        }
      },
      async loadCountries() {
        if (this.countries.length === 0) {
          try {
            this.countries = await this.$api.lookupCountries();
          } catch (error) {
            this.errorMessage = this.$api.processError(error);
          }
        }
      },
      async loadDisciplines() {
        if (this.disciplines.length === 0) {
          try {
            this.disciplines = await this.$api.lookupDisciplines();
          } catch (error) {
            this.errorMessage = this.$api.processError(error);
          }
        }
      },
      async loadSpecialities() {
        if (this.user.hcp.discipline) {
          this.user.hcp.speciality = undefined;
          this.user.hcp.role = undefined;

          try {
            this.specialities = await this.$api.lookupSpecialities(this.user.hcp.discipline.id);
          } catch (error) {
            this.errorMessage = this.$api.processError(error);
          }
        }
      },
      async loadRoles() {
        if (this.user.hcp.speciality) {
          this.user.hcp.role = undefined;

          try {
            this.roles = await this.$api.lookupRolesUd(this.user.hcp.speciality.id);
          } catch (error) {
            this.errorMessage = this.$api.processError(error);
          }
        }
      },
      async save() {
        switch (this.usernameType) {
          case 'EMAIL':
            this.user.username = this.user.emailAddress;
            break;
          case 'CELLULAR':
            this.user.username = this.user.cellularNumber;
            break;
          default:
            break;
        }

        this.$v.$touch();

        if (this.$v.$invalid) {
          this.errorMessage = this.$api.validationError;
          window.scrollTo(0, 0);
        } else {
          this.clearIdentificationData();

          try {
            this.loading = true;
            await this.$api.register(this.user);
          } catch (error) {
            this.errorMessage = this.$api.processError(error);
            window.scrollTo(0, 0);
          } finally {
            this.loading = false;
          }
        }
      },
      openModal() {
        this.modalOpen = true;
      },
      closeModal() {
        this.modalOpen = false;
      },
    },
    async mounted() {
      this.loadCountries();
      this.loadDisciplines();

      if (this.$route.query.inviteCode) {
        try {
          const invite = await this.$api.fetchPracticeInviteData(this.$route.query.inviteCode);

          if (invite.status === 'SENT') {
            this.user.inviteCode = invite.inviteCode;
            this.user.type = invite.userType;
          } else if (invite.status === 'ACCEPTED' || invite.status === 'REGISTERED') {
            this.$router.push('/login');
          } else {
            this.errorMessage = `Invite cannot be accepted, status of invite currently is ${invite.status}`;
          }
        } catch (error) {
          if (error.response) {
            this.errorMessage = 'Unable to retrieve invite data';
          } else {
            this.errorMessage = this.$api.communicationError;
          }
        }
      }
    },
  };

</script>



// WEBPACK FOOTER //
// PracticeForm.vue?9af2a4e2